/**
 * May a tenant who OPENS a /b/ link already carrying `step=banking` see the banking screen?
 * Pure (no React, no router) so it runs under `node --test`.
 *
 * WHY: HomeScreen asks pre-flight only when the URL has no `step` (or `step=sorry`). A tenant
 * the server has already marked not eligible — e.g. work details left them with no way to
 * continue, so cpcore set `is_eligible:false` and re-locked BANKING to PENDING/LOCKED — who
 * reloads a URL that still says `step=banking` skipped pre-flight and landed on banking anyway.
 *
 * The rule (server data only, no lender names or lender statuses):
 *   pre-flight answered (`status` truthy) AND `data.is_eligible === false` (strictly)
 *   AND the journey HAS a BANKING step that is PENDING and not UNLOCKED
 *   → the same screen a step-less reload would pick for a not-eligible tenant.
 * Anything else — eligible, unknown, no answer, no journey, banking unlocked or already
 * engaged (WAITING/IN_REVIEW/FAILED/…) — returns null: stay on banking, exactly as before.
 * Failing open matters: a missing answer must never strand a tenant who may do banking.
 */

/**
 * The step pre-flight sends a not-eligible tenant to (HomeScreen's `isEligible === false`
 * branch). Shared so a step-less reload and a `step=banking` reload always agree.
 *
 * @param {object | null | undefined} lead  pre-flight `data`
 * @param {string | null | undefined} partner  partner tag
 * @param {Record<string, Record<string, string>>} rules  `navigationRules` (partnerJourney.jsx)
 * @returns {string}
 */
export function notEligibleStep(lead, partner, rules) {
  // Server-derived (pre-flight `sorryReason`, from the open journey's steps): the penny drop
  // failed on the name-mismatch cap, so refresh/resume must land on the penny-drop sorry
  // screen rather than the generic one.
  if (lead?.sorryReason === "account-name") return "penny-drop-sorry";
  if (lead?.isStudent ?? null) return "guarantor-details";
  return rules[partner]?.notHardEligible || rules.default.notHardEligible;
}

/**
 * @param {{ preflight?: object | null, journey?: object | null, partner?: string,
 *           rules: Record<string, Record<string, string>> }} input
 *   `preflight` is the raw `b2b/onboard/pre-flight` response, `journey` is `journey/full`'s
 *   `journey`, `partner` the URL partner (pre-flight's `tag` wins when present).
 * @returns {{ step: string, partner: string } | null}  where to go instead, or null to stay
 */
export function bankingEntryRedirect({ preflight, journey, partner, rules }) {
  if (!preflight?.status) return null;
  const lead = preflight.data;
  if (lead?.is_eligible !== false) return null;
  const steps = Array.isArray(journey?.steps) ? journey.steps : null;
  if (!steps) return null;
  const banking = steps.find((s) => s?.name === "BANKING");
  if (!banking) return null;
  const status = typeof banking.status === "string" ? banking.status.toUpperCase() : "";
  if (status !== "PENDING" || banking.state === "UNLOCKED") return null;
  const tag = preflight.tag || partner || "";
  return { step: notEligibleStep(lead, tag, rules), partner: tag };
}
